import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { supabase } from "@/integrations/supabase/client";
import { useLearnerContext } from "@/lib/learner-context";
import {
  Trophy, Flame, Star, Sparkles, Award, Target, Zap, Gift, Crown, Medal,
  BookOpen, ClipboardList, Library, TreeDeciduous, GraduationCap,
  Footprints, Lightbulb, Sticker, Frame, Palette, Loader2, Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { celebrate } from "@/components/makai/celebration";
import { Celebrate } from "@/components/celebrate";
import {
  levelFromXp, ensureDailyMissions, completeMission, checkAndGrantBadges, weekStart, awardXp,
} from "@/lib/achievements";

export const Route = createFileRoute("/learner/achievements")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Achievements — MAKAI" }] }),
  component: AchievementsPage,
});

const ICONS: Record<string, typeof Trophy> = {
  Trophy, Flame, Star, Sparkles, Award, Target, Zap, Gift, Crown, Medal,
  BookOpen, ClipboardList, Library, TreeDeciduous, GraduationCap,
  Footprints, Lightbulb, Sticker, Frame, Palette,
};
const Icon = ({ name, className }: { name?: string; className?: string }) => {
  const C = ICONS[name ?? "Award"] ?? Award;
  return <C className={className} />;
};

type Tab = "overview" | "missions" | "challenges" | "badges" | "leaderboard" | "rewards" | "certificates";
const TABS: { id: Tab; label: string; icon: typeof Trophy }[] = [
  { id: "overview",     label: "Overview",     icon: Sparkles },
  { id: "missions",     label: "Missions",     icon: Target },
  { id: "challenges",   label: "Challenges",   icon: Trophy },
  { id: "badges",       label: "Badges",       icon: Medal },
  { id: "leaderboard",  label: "Leaderboards", icon: Crown },
  { id: "rewards",      label: "Rewards",      icon: Gift },
  { id: "certificates", label: "Certificates", icon: Award },
];

function AchievementsPage() {
  const { ctx } = useLearnerContext();
  const [tab, setTab] = useState<Tab>("overview");
  const [celebration, setCelebration] = useState<{ title: string; subtitle?: string } | null>(null);

  return (
    <AppShell role="learner" title="Achievements" subtitle="Earn XP, level up, and collect rewards as you learn.">
      <Celebrate open={!!celebration} title={celebration?.title ?? ""} subtitle={celebration?.subtitle} onClose={() => setCelebration(null)} />

      <div className="mb-5 flex gap-1.5 overflow-x-auto rounded-full border border-border bg-card p-1 scrollbar-none">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors",
              tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {!ctx ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-border bg-card p-10 text-sm text-muted-foreground">
          <Loader2 className="mb-2 h-5 w-5 animate-spin" /> Loading your profile…
        </div>
      ) : (
        <>
          {tab === "overview"     && <Overview learnerId={ctx.learnerId} onCelebrate={setCelebration} />}
          {tab === "missions"     && <Missions learnerId={ctx.learnerId} onCelebrate={setCelebration} />}
          {tab === "challenges"   && <Challenges learnerId={ctx.learnerId} schoolId={ctx.schoolId} />}
          {tab === "badges"       && <Badges learnerId={ctx.learnerId} />}
          {tab === "leaderboard"  && <Leaderboards learnerId={ctx.learnerId} schoolId={ctx.schoolId} streamId={ctx.streamId} />}
          {tab === "rewards"      && <Rewards learnerId={ctx.learnerId} onCelebrate={setCelebration} />}
          {tab === "certificates" && <Certificates learnerId={ctx.learnerId} />}
        </>
      )}
    </AppShell>
  );
}

/* ------------------------- OVERVIEW ------------------------- */
function Overview({ learnerId, onCelebrate }: { learnerId: string; onCelebrate: (c: { title: string; subtitle?: string }) => void }) {
  const [xp, setXp] = useState<{ points: number; current_streak: number; longest_streak: number } | null>(null);
  const [badgeCount, setBadgeCount] = useState(0);
  const [missionCount, setMissionCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [x, b, m] = await Promise.all([
        supabase.from("learner_xp").select("points,current_streak,longest_streak").eq("learner_id", learnerId).maybeSingle(),
        supabase.from("learner_badges").select("id", { count: "exact", head: true }).eq("learner_id", learnerId),
        supabase.from("daily_missions").select("id", { count: "exact", head: true }).eq("learner_id", learnerId).not("completed_at", "is", null),
      ]);
      if (cancelled) return;
      setXp(x.data ?? { points: 0, current_streak: 0, longest_streak: 0 });
      setBadgeCount(b.count ?? 0);
      setMissionCount(m.count ?? 0);
      const granted = await checkAndGrantBadges(learnerId);
      if (granted.length > 0 && !cancelled) onCelebrate({ title: "New badge unlocked!", subtitle: granted.join(", ") });
    })();
    return () => { cancelled = true; };
  }, [learnerId, onCelebrate]);

  if (!xp) return <Skeleton />;
  const lvl = levelFromXp(xp.points);

  return (
    <div className="space-y-5">
      {/* Hero level card */}
      <article className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-card to-card p-6 shadow-soft">
        <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="grid h-20 w-20 place-items-center rounded-2xl bg-primary/15 text-primary shadow-inner">
              <div className="text-center">
                <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">Lvl</div>
                <div className="font-display text-2xl font-black leading-none">{lvl.level}</div>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-primary">{lvl.title}</div>
              <div className="font-display text-2xl font-bold">{xp.points.toLocaleString()} XP</div>
              <div className="text-xs text-muted-foreground">{lvl.span - lvl.intoLevel} XP to level {lvl.level + 1}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatPill icon={Flame} label="Streak" value={`${xp.current_streak}d`} tone="clay" />
            <StatPill icon={Medal} label="Badges" value={badgeCount} tone="gold" />
            <StatPill icon={Target} label="Missions" value={missionCount} tone="sky" />
          </div>
        </div>
        <div className="relative mt-5">
          <Progress value={lvl.progress} className="h-3 bg-primary/15" />
          <div className="mt-1.5 flex justify-between text-[10px] font-semibold text-muted-foreground">
            <span>Level {lvl.level}</span>
            <span>{lvl.progress}%</span>
            <span>Level {lvl.level + 1}</span>
          </div>
        </div>
      </article>

      {/* Quick stats grid */}
      <div className="grid gap-3 sm:grid-cols-3">
        <InfoCard icon={Flame} label="Longest streak" value={`${xp.longest_streak} days`} />
        <InfoCard icon={Zap} label="Total XP" value={xp.points.toLocaleString()} />
        <InfoCard icon={Trophy} label="Rank title" value={lvl.title} />
      </div>
    </div>
  );
}

function StatPill({ icon: I, label, value, tone }: { icon: typeof Trophy; label: string; value: string | number; tone: "gold" | "sky" | "clay" }) {
  const bg = tone === "gold" ? "bg-primary/15 text-primary" : tone === "sky" ? "bg-sky-500/15 text-sky-500" : "bg-orange-500/15 text-orange-500";
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-card/70 px-3 py-2">
      <div className={cn("grid h-8 w-8 place-items-center rounded-lg", bg)}><I className="h-4 w-4" /></div>
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm font-bold">{value}</div>
      </div>
    </div>
  );
}
function InfoCard({ icon: I, label, value }: { icon: typeof Trophy; label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <I className="h-5 w-5 text-primary" />
      <div className="mt-2 text-xs text-muted-foreground">{label}</div>
      <div className="font-display text-xl font-bold">{value}</div>
    </div>
  );
}
function Skeleton() {
  return <div className="grid h-40 place-items-center rounded-2xl border border-dashed border-border bg-card text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>;
}

/* ------------------------- MISSIONS ------------------------- */
interface Mission { id: string; code: string; title: string; description: string | null; icon: string | null; target: number; progress: number; xp_reward: number; completed_at: string | null }
function Missions({ learnerId, onCelebrate }: { learnerId: string; onCelebrate: (c: { title: string; subtitle?: string }) => void }) {
  const [missions, setMissions] = useState<Mission[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    await ensureDailyMissions(learnerId);
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase
      .from("daily_missions")
      .select("id,code,title,description,icon,target,progress,xp_reward,completed_at")
      .eq("learner_id", learnerId)
      .eq("mission_date", today)
      .order("created_at");
    setMissions((data ?? []) as Mission[]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [learnerId]);

  const claim = async (m: Mission) => {
    if (m.completed_at) return;
    setBusy(m.id);
    try {
      await completeMission(m.id, learnerId, m.xp_reward);
      onCelebrate({ title: `+${m.xp_reward} XP`, subtitle: m.title });
      celebrate({ type: "mission", title: `Mission complete · +${m.xp_reward} XP`, message: m.title });
      await load();
      const granted = await checkAndGrantBadges(learnerId);
      if (granted.length > 0) {
        toast.success(`New badge: ${granted.join(", ")}`);
        celebrate({ type: "badge", title: "New badge unlocked!", message: granted.join(", ") });
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setBusy(null); }
  };

  if (!missions) return <Skeleton />;
  const done = missions.filter((m) => m.completed_at).length;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold">Today's missions</h3>
            <p className="text-xs text-muted-foreground">Complete all three for a streak boost.</p>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl font-bold">{done}<span className="text-muted-foreground">/{missions.length}</span></div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Completed</div>
          </div>
        </div>
        <Progress value={(done / Math.max(missions.length, 1)) * 100} className="mt-3 h-2" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {missions.map((m) => {
          const done = !!m.completed_at;
          return (
            <article key={m.id} className={cn("rounded-2xl border p-4 transition-all", done ? "border-primary/40 bg-primary/5" : "border-border bg-card shadow-soft hover:border-primary/30")}>
              <div className="flex items-start gap-3">
                <div className={cn("grid h-11 w-11 place-items-center rounded-xl", done ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary")}>
                  <Icon name={m.icon ?? "Target"} className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="truncate font-semibold">{m.title}</h4>
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">+{m.xp_reward} XP</span>
                  </div>
                  {m.description && <p className="mt-1 text-xs text-muted-foreground">{m.description}</p>}
                  <Button
                    size="sm"
                    variant={done ? "secondary" : "default"}
                    disabled={done || busy === m.id}
                    onClick={() => claim(m)}
                    className="mt-3 h-8"
                  >
                    {busy === m.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                    {done ? "Completed" : "Mark as done"}
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------- WEEKLY CHALLENGES ------------------------- */
interface Challenge { id: string; title: string; description: string | null; icon: string | null; target_value: number; xp_reward: number }
interface ChallengeProgress { challenge_id: string; progress: number; completed_at: string | null }
function Challenges({ learnerId, schoolId }: { learnerId: string; schoolId: string }) {
  const [items, setItems] = useState<Challenge[] | null>(null);
  const [progress, setProgress] = useState<Record<string, ChallengeProgress>>({});

  useEffect(() => {
    (async () => {
      const ws = weekStart();
      let q = supabase.from("weekly_challenges").select("id,title,description,icon,target_value,xp_reward").eq("week_start", ws);
      if (schoolId) q = q.or(`school_id.eq.${schoolId},school_id.is.null`);
      const { data: ch } = await q;
      setItems(ch ?? []);
      if (ch && ch.length) {
        const { data: prog } = await supabase
          .from("learner_challenge_progress")
          .select("challenge_id,progress,completed_at")
          .eq("learner_id", learnerId)
          .in("challenge_id", ch.map((c) => c.id));
        const map: Record<string, ChallengeProgress> = {};
        for (const p of prog ?? []) map[p.challenge_id] = p as ChallengeProgress;
        setProgress(map);
      }
    })();
  }, [learnerId, schoolId]);

  if (!items) return <Skeleton />;
  if (items.length === 0) return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
      <Trophy className="mx-auto h-10 w-10 text-muted-foreground/40" />
      <h3 className="mt-2 font-display text-base font-bold">No active challenges this week</h3>
      <p className="text-xs text-muted-foreground">Check back next Monday for fresh weekly goals.</p>
    </div>
  );

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {items.map((c) => {
        const p = progress[c.id];
        const pct = p ? Math.min(100, Math.round((p.progress / c.target_value) * 100)) : 0;
        const done = !!p?.completed_at;
        return (
          <article key={c.id} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <div className="flex items-start gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/15 text-primary">
                <Icon name={c.icon ?? "Trophy"} className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-semibold">{c.title}</h4>
                  <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">+{c.xp_reward} XP</span>
                </div>
                {c.description && <p className="mt-1 text-xs text-muted-foreground">{c.description}</p>}
              </div>
            </div>
            <div className="mt-3">
              <Progress value={pct} className="h-2" />
              <div className="mt-1.5 flex justify-between text-[10px] font-semibold text-muted-foreground">
                <span>{p?.progress ?? 0} / {c.target_value}</span>
                <span>{done ? "Completed 🎉" : `${pct}%`}</span>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

/* ------------------------- BADGES ------------------------- */
interface Badge { id: string; code: string; name: string; description: string | null; icon: string | null; tint: string | null }
function Badges({ learnerId }: { learnerId: string }) {
  const [all, setAll] = useState<Badge[]>([]);
  const [earned, setEarned] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [{ data: badges }, { data: lb }] = await Promise.all([
        supabase.from("badges").select("id,code,name,description,icon,tint").order("created_at"),
        supabase.from("learner_badges").select("badge_id,earned_at").eq("learner_id", learnerId),
      ]);
      setAll(badges ?? []);
      setEarned(new Set((lb ?? []).map((b) => b.badge_id)));
      setLoading(false);
    })();
  }, [learnerId]);

  if (loading) return <Skeleton />;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-4 text-sm">
        <span className="font-semibold">{earned.size}</span><span className="text-muted-foreground"> of {all.length} badges earned</span>
      </div>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
        {all.map((b) => {
          const got = earned.has(b.id);
          return (
            <article key={b.id} className={cn("rounded-2xl border p-4 text-center transition-all", got ? "border-primary/40 bg-primary/5" : "border-border bg-card opacity-60")}>
              <div className={cn("mx-auto grid h-14 w-14 place-items-center rounded-2xl", got ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                {got ? <Icon name={b.icon ?? "Award"} className="h-7 w-7" /> : <Lock className="h-6 w-6" />}
              </div>
              <h4 className="mt-2 text-sm font-bold">{b.name}</h4>
              <p className="mt-1 text-[11px] text-muted-foreground line-clamp-2">{b.description}</p>
            </article>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------- LEADERBOARDS ------------------------- */
type Scope = "class" | "school" | "global";
interface LbRow { learner_id: string; points: number; first_name: string | null; last_name: string | null }
function Leaderboards({ learnerId, schoolId, streamId }: { learnerId: string; schoolId: string; streamId: string | null }) {
  const [scope, setScope] = useState<Scope>(streamId ? "class" : schoolId ? "school" : "global");
  const [rows, setRows] = useState<LbRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRows(null);
      let learnerQuery = supabase.from("learners").select("id");
      if (scope === "class" && streamId) learnerQuery = learnerQuery.eq("current_stream_id", streamId);
      else if (scope === "school" && schoolId) learnerQuery = learnerQuery.eq("school_id", schoolId);
      const { data: lrns } = await learnerQuery.limit(500);
      const ids = (lrns ?? []).map((l) => l.id);
      if (cancelled) return;
      if (ids.length === 0) { setRows([]); return; }
      const { data: xp } = await supabase
        .from("learner_xp")
        .select("learner_id,points")
        .in("learner_id", ids)
        .order("points", { ascending: false })
        .limit(50);
      const xpIds = (xp ?? []).map((x) => x.learner_id);
      const { data: profiles } = await supabase
        .from("learners")
        .select("id,first_name,last_name")
        .in("id", xpIds.length ? xpIds : ids.slice(0, 1));
      const pmap = new Map((profiles ?? []).map((p) => [p.id, p]));
      const merged: LbRow[] = (xp ?? []).map((x) => ({
        learner_id: x.learner_id,
        points: x.points,
        first_name: pmap.get(x.learner_id)?.first_name ?? null,
        last_name: pmap.get(x.learner_id)?.last_name ?? null,
      }));
      if (!cancelled) setRows(merged);
    })();
    return () => { cancelled = true; };
  }, [scope, schoolId, streamId]);

  const myRank = useMemo(() => rows?.findIndex((r) => r.learner_id === learnerId) ?? -1, [rows, learnerId]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {(["class", "school", "global"] as Scope[]).map((s) => {
          const disabled = (s === "class" && !streamId) || (s === "school" && !schoolId);
          return (
            <button
              key={s}
              type="button"
              disabled={disabled}
              onClick={() => setScope(s)}
              className={cn(
                "rounded-full border px-4 py-1.5 text-xs font-semibold capitalize transition-colors",
                scope === s ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card",
                disabled && "opacity-40"
              )}
            >{s} ranking</button>
          );
        })}
      </div>

      {myRank >= 0 && (
        <div className="rounded-2xl border border-primary/40 bg-primary/5 px-4 py-3 text-sm">
          <span className="font-bold text-primary">Your rank: #{myRank + 1}</span>
          <span className="ml-2 text-muted-foreground">with {rows![myRank].points.toLocaleString()} XP</span>
        </div>
      )}

      {!rows ? <Skeleton /> : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No learners yet on this leaderboard.
        </div>
      ) : (
        <ol className="overflow-hidden rounded-2xl border border-border bg-card">
          {rows.map((r, i) => {
            const me = r.learner_id === learnerId;
            const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
            return (
              <li key={r.learner_id} className={cn("flex items-center gap-3 border-b border-border/60 px-4 py-3 last:border-0", me && "bg-primary/5")}>
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-sm font-bold">
                  {medal ?? `#${i + 1}`}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">
                    {r.first_name ?? "Learner"} {r.last_name ?? ""} {me && <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-[10px] text-primary-foreground">You</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-base font-bold">{r.points.toLocaleString()}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">XP</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/* ------------------------- REWARD STORE ------------------------- */
interface RewardItem { id: string; name: string; description: string | null; icon: string | null; cost_xp: number; active: boolean }
function Rewards({ learnerId, onCelebrate }: { learnerId: string; onCelebrate: (c: { title: string; subtitle?: string }) => void }) {
  const [items, setItems] = useState<RewardItem[] | null>(null);
  const [balance, setBalance] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    const [{ data: it }, { data: x }] = await Promise.all([
      supabase.from("reward_items").select("id,name,description,icon,cost_xp,active").eq("active", true).order("cost_xp"),
      supabase.from("learner_xp").select("points").eq("learner_id", learnerId).maybeSingle(),
    ]);
    setItems(it ?? []);
    setBalance(x?.points ?? 0);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [learnerId]);

  const redeem = async (item: RewardItem) => {
    if (balance < item.cost_xp) { toast.error("Not enough XP yet — keep learning!"); return; }
    setBusy(item.id);
    try {
      const { error } = await supabase.rpc("redeem_reward", { _item: item.id });
      if (error) throw error;
      onCelebrate({ title: "Reward redeemed!", subtitle: item.name });
      await load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(null); }
  };

  if (!items) return <Skeleton />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/15 to-transparent p-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-primary">Your balance</div>
          <div className="font-display text-2xl font-bold">{balance.toLocaleString()} XP</div>
        </div>
        <Zap className="h-8 w-8 text-primary" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => {
          const can = balance >= it.cost_xp;
          return (
            <article key={it.id} className="flex flex-col rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
                <Icon name={it.icon ?? "Gift"} className="h-6 w-6" />
              </div>
              <h4 className="mt-3 font-semibold">{it.name}</h4>
              <p className="mt-1 flex-1 text-xs text-muted-foreground">{it.description}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-display text-lg font-bold text-primary">{it.cost_xp} XP</span>
                <Button size="sm" disabled={!can || busy === it.id} onClick={() => redeem(it)}>
                  {busy === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : can ? "Redeem" : "Locked"}
                </Button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------- CERTIFICATES ------------------------- */
interface Cert { id: string; kind: string; title: string; subtitle: string | null; issued_at: string }
function Certificates({ learnerId }: { learnerId: string }) {
  const [certs, setCerts] = useState<Cert[] | null>(null);
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("certificates")
        .select("id,kind,title,subtitle,issued_at")
        .eq("learner_id", learnerId)
        .order("issued_at", { ascending: false });
      setCerts((data ?? []) as Cert[]);
    })();
  }, [learnerId]);

  if (!certs) return <Skeleton />;
  if (certs.length === 0) return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
      <Award className="mx-auto h-10 w-10 text-muted-foreground/40" />
      <h3 className="mt-2 font-display text-base font-bold">No certificates yet</h3>
      <p className="text-xs text-muted-foreground">Complete subjects and milestones to earn personalised certificates.</p>
    </div>
  );

  const printCert = (c: Cert) => {
    const html = `<!doctype html><html><head><title>${c.title}</title><style>
      body{font-family:Georgia,serif;display:grid;place-items:center;height:100vh;margin:0;background:#F7F6F0;color:#0B1736}
      .cert{width:760px;padding:64px;border:6px double #C8A03C;background:white;text-align:center;box-shadow:0 30px 60px -30px rgba(0,0,0,.3)}
      h1{font-size:14px;letter-spacing:8px;color:#C8A03C;margin:0 0 24px}
      h2{font-size:42px;margin:0 0 16px}
      p{font-size:18px;margin:8px 0;color:#1D1D1D}
      .seal{margin-top:32px;font-size:12px;color:#666;letter-spacing:2px}
    </style></head><body><div class="cert">
      <h1>CERTIFICATE OF ACHIEVEMENT</h1>
      <h2>${c.title}</h2>
      ${c.subtitle ? `<p>${c.subtitle}</p>` : ""}
      <p style="margin-top:32px">Issued on ${new Date(c.issued_at).toLocaleDateString()}</p>
      <div class="seal">— MAKAI · Learn. Dream. Grow. —</div>
    </div></body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {certs.map((c) => (
        <article key={c.id} className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-card p-5 shadow-soft">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/15 blur-2xl" />
          <Award className="h-8 w-8 text-primary" />
          <h4 className="mt-3 font-display text-lg font-bold">{c.title}</h4>
          {c.subtitle && <p className="text-xs text-muted-foreground">{c.subtitle}</p>}
          <div className="mt-3 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{new Date(c.issued_at).toLocaleDateString()}</span>
            <Button size="sm" variant="outline" onClick={() => printCert(c)}>Print / PDF</Button>
          </div>
        </article>
      ))}
    </div>
  );
}
